import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useImpersonation } from "../context/ImpersonationContext";
import api from "../lib/api";
import DisclaimerBanner from "./DisclaimerBanner";
import EulaReacceptModal from "./EulaReacceptModal";
import DisclaimerModal from "./DisclaimerModal";
import BrandLogo from "./BrandLogo";
import {
    House,
    Folders,
    Plus,
    Calculator as Calc,
    SignOut,
    Users,
    CalendarCheck,
    Coins,
    ChartLineUp,
    CreditCard,
    Vault as VaultIcon,
    HandHeart,
    Question,
    PaperPlaneTilt,
    Coins as CoinsIcon,
    Wrench,
    UserSwitch,
    X,
} from "@phosphor-icons/react";

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const { impersonation, stop: stopImpersonation } = useImpersonation();
    const loc = useLocation();
    const nav = useNavigate();
    const [awaitingCount, setAwaitingCount] = useState(0);

    useEffect(() => {
        if (!user) return;
        let alive = true;
        api.get("/offers")
            .then((r) => {
                if (!alive) return;
                const n = (r.data || []).filter((o) => o.status === "sent").length;
                setAwaitingCount(n);
            })
            .catch(() => {});
        return () => { alive = false; };
    }, [user, loc.pathname]);

    const navItems = [
        { to: "/dashboard", label: "Overview", icon: House, testId: "nav-dashboard" },
        { to: "/cases", label: user?.role === "business" ? "Client Cases" : "My Cases", icon: Folders, testId: "nav-cases" },
        { to: "/cases/new", label: "New Case", icon: Plus, testId: "nav-new-case" },
        { to: "/deadlines", label: "Deadlines", icon: CalendarCheck, testId: "nav-deadlines" },
        ...(user?.role === "business"
            ? [{ to: "/clients", label: "Clients", icon: Users, testId: "nav-clients" }]
            : []),
        { to: "/judgment-calculator", label: "Judgment Interest Calculator", icon: Coins, testId: "nav-judgment-calc" },
        { to: "/calculator", label: "Payment Plan", icon: Calc, testId: "nav-calculator" },
        { to: "/intelligence", label: "Settlements", icon: ChartLineUp, testId: "nav-intelligence" },
        { to: "/offers", label: "View Offers", icon: PaperPlaneTilt, testId: "nav-offers", badge: awaitingCount },
        { to: "/credits", label: "Mailing Credits", icon: CoinsIcon, testId: "nav-credits" },
        { to: "/vault", label: "Documents", icon: VaultIcon, testId: "nav-vault" },
        { to: "/referrals", label: "Referrals", icon: HandHeart, testId: "nav-referrals" },
        { to: "/help/lookup", label: "Help", icon: Question, testId: "nav-help" },
        { to: "/billing", label: "Billing & Plan", icon: CreditCard, testId: "nav-billing" },
        ...(user?.role === "business"
            ? [{ to: "/admin/creditors", label: "Directory admin", icon: Wrench, testId: "nav-admin-creditors" }]
            : []),
    ];

    return (
        <div className="min-h-screen bg-white text-slate-900 font-sans">
            {impersonation && (
                <div
                    data-testid="impersonation-banner"
                    className="bg-amber-500 text-slate-900 border-b border-amber-600"
                >
                    <div className="max-w-[1400px] mx-auto px-6 py-2 flex items-center justify-between gap-4 text-sm">
                        <div className="flex items-center gap-2 min-w-0">
                            <UserSwitch size={16} weight="bold" />
                            <span className="font-medium">Client focus mode:</span>
                            <span className="truncate" data-testid="impersonation-banner-name">{impersonation.name}</span>
                            <span className="hidden sm:inline text-amber-900/70">— all views filtered to this client&apos;s cases only.</span>
                        </div>
                        <button
                            type="button"
                            data-testid="impersonation-exit-btn"
                            onClick={() => { stopImpersonation(); window.location.reload(); }}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-slate-900 text-white hover:bg-slate-800 text-xs whitespace-nowrap"
                        >
                            <X size={12} weight="bold" /> Exit
                        </button>
                    </div>
                </div>
            )}
            <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/80 backdrop-blur-xl">
                <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
                    <Link to="/dashboard" className="flex items-center gap-2" data-testid="header-brand">
                        <BrandLogo height={36} />
                    </Link>
                    <div className="flex items-center gap-6">
                        <div className="hidden md:flex items-center gap-2 text-xs">
                            <span className="overline">{user?.role === "business" ? "Business" : "Individual"}</span>
                            <span className="text-slate-300">|</span>
                            <span data-testid="header-user-name" className="text-slate-700">{user?.name}</span>
                        </div>
                        <button
                            data-testid="logout-btn"
                            onClick={() => { logout(); nav("/"); }}
                            className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 transition-colors"
                        >
                            <SignOut size={16} /> Logout
                        </button>
                    </div>
                </div>
            </header>

            <div className="max-w-[1400px] mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8">
                <aside className="lg:sticky lg:top-24 self-start">
                    <nav className="flex lg:flex-col gap-1 overflow-x-auto bg-[#0F1E3D] p-2 rounded-md" data-testid="sidebar-nav">
                        {navItems.map((it) => {
                            const Icon = it.icon;
                            const active = loc.pathname === it.to ||
                                (it.to === "/cases" && loc.pathname.startsWith("/cases") && loc.pathname !== "/cases/new") ||
                                (it.to === "/clients" && loc.pathname.startsWith("/clients"));
                            return (
                                <Link
                                    key={it.to}
                                    to={it.to}
                                    data-testid={it.testId}
                                    className={`group flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm transition-colors whitespace-nowrap ${
                                        active
                                            ? "bg-white text-[#0F1E3D] font-medium"
                                            : "text-slate-200 hover:bg-white/10 hover:text-white"
                                    }`}
                                >
                                    <Icon size={16} weight={active ? "fill" : "regular"} />
                                    <span className="flex-1">{it.label}</span>
                                    {it.badge ? (
                                        <span
                                            data-testid={`${it.testId}-badge`}
                                            className={`inline-flex min-w-[20px] h-5 px-1.5 items-center justify-center rounded-full text-[11px] font-semibold ${
                                                active ? "bg-rose-100 text-rose-700" : "bg-rose-500 text-white"
                                            }`}
                                        >
                                            {it.badge}
                                        </span>
                                    ) : null}
                                </Link>
                            );
                        })}
                    </nav>
                </aside>

                <main className="min-w-0 pb-24">{children}</main>
            </div>
            <DisclaimerBanner />
            <EulaReacceptModal />
            <DisclaimerModal />
        </div>
    );
}
